/*
  assets/facets.js — FACETS-RUNTIME-05
  Owner: existing Shopify collection filtering, sorting and active-filter runtime.

  Scope:
  - <facet-filters-form>
  - <price-range>
  - <facet-remove>
  - #ProductGridContainer / #product-grid
  - server-rendered section HTML for the current same-origin pathname

  Boundary:
  - No Global Search / Place diagnosis, product, checkout, GFun, API or delivery ownership.
  - Filter state remains URL/query-string based and server authoritative.
  - Browser and Shopify section rendering use the current native pathname unchanged.
  - No /search <-> /place route projection is permitted here.
*/
(function () {
  'use strict';

  var VERSION = '2026-09-05-facets-runtime-05';
  var CACHE_LIMIT = 20;

  function createDebounce(callback, wait) {
    var timer = null;

    function debounced() {
      var context = this;
      var args = arguments;
      window.clearTimeout(timer);
      timer = window.setTimeout(function () {
        timer = null;
        callback.apply(context, args);
      }, wait);
    }

    debounced.cancel = function () {
      window.clearTimeout(timer);
      timer = null;
    };

    return debounced;
  }

  function cleanSearchParams(value) {
    var params = new URLSearchParams(String(value || '').replace(/^\?/, ''));
    params.delete('section_id');
    return params.toString();
  }

  function currentPathname() {
    return String(window.location.pathname || '/');
  }

  function pageUrl(searchParams) {
    var query = cleanSearchParams(searchParams);
    return currentPathname() + (query ? '?' + query : '');
  }

  function sectionUrl(sectionId, searchParams) {
    var url = new URL(currentPathname(), window.location.origin);
    var params = new URLSearchParams(cleanSearchParams(searchParams));
    params.forEach(function (value, key) {
      url.searchParams.append(key, value);
    });
    url.searchParams.set('section_id', sectionId);
    return url.pathname + url.search;
  }

  function parseHTML(html) {
    return new DOMParser().parseFromString(String(html || ''), 'text/html');
  }

  function emit(name, detail) {
    try {
      document.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
    } catch (_error) {}
  }

  function setLoading(active) {
    var productGridContainer = document.getElementById('ProductGridContainer');
    var collection = productGridContainer
      ? productGridContainer.querySelector('.collection')
      : null;
    var countContainer = document.getElementById('ProductCount');
    var countContainerDesktop = document.getElementById('ProductCountDesktop');

    document
      .querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner')
      .forEach(function (spinner) {
        spinner.classList.toggle('hidden', !active);
      });

    if (collection) collection.classList.toggle('loading', active);
    if (countContainer) countContainer.classList.toggle('loading', active);
    if (countContainerDesktop) countContainerDesktop.classList.toggle('loading', active);

    document.documentElement.setAttribute(
      'data-facets-request-state',
      active ? 'loading' : 'idle'
    );
  }

  class FacetFiltersForm extends HTMLElement {
    constructor() {
      super();
      this.form = null;
      this.facetWrapper = null;
      this.abortController = null;
      this.cleanups = [];
      this.bound = false;
      this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
      this.onSubmitHandler = this.onSubmitHandler.bind(this);
      this.debouncedOnSubmit = createDebounce(this.onSubmitHandler, 300);
    }

    connectedCallback() {
      if (this.bound) return;

      this.form = this.querySelector('form');
      if (!this.form) {
        this.setAttribute('data-facets-runtime-state', 'missing-form');
        return;
      }

      this.bound = true;
      this.abortController = typeof AbortController === 'function'
        ? new AbortController()
        : null;
      this.facetWrapper = this.querySelector('#FacetsWrapperDesktop');

      this.addManagedListener(this.form, 'input', this.debouncedOnSubmit);
      if (this.facetWrapper && typeof window.onKeyUpEscape === 'function') {
        this.addManagedListener(this.facetWrapper, 'keyup', window.onKeyUpEscape);
      }

      this.setAttribute('data-facets-runtime-state', 'ready');
      this.setAttribute('data-facets-runtime-version', VERSION);
      FacetFiltersForm.ensureHistoryListener();
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
      this.cleanups.forEach(function (cleanup) {
        cleanup();
      });
      this.cleanups = [];
      this.abortController = null;
      this.bound = false;
      this.debouncedOnSubmit.cancel();
    }

    addManagedListener(target, type, listener, options) {
      if (!target || typeof listener !== 'function') return;

      var listenerOptions = this.abortController
        ? Object.assign({}, options || {}, { signal: this.abortController.signal })
        : options;

      try {
        target.addEventListener(type, listener, listenerOptions);
      } catch (_error) {
        target.addEventListener(type, listener, options);
        this.cleanups.push(function () {
          target.removeEventListener(type, listener, options);
        });
      }
    }

    static ensureHistoryListener() {
      if (FacetFiltersForm.historyListenerInstalled) return;
      FacetFiltersForm.historyListenerInstalled = true;

      window.addEventListener('popstate', function (event) {
        var stateParams = event.state && typeof event.state.searchParams === 'string'
          ? event.state.searchParams
          : window.location.search.slice(1);
        var searchParams = cleanSearchParams(stateParams);

        if (searchParams === FacetFiltersForm.searchParamsPrev) return;
        FacetFiltersForm.renderPage(searchParams, null, false);
      });
    }

    static setListeners() {
      FacetFiltersForm.ensureHistoryListener();
    }

    static toggleActiveFacets(disable) {
      var isDisabled = disable !== false;

      document.querySelectorAll('.js-facet-remove').forEach(function (element) {
        element.classList.toggle('disabled', isDisabled);
        element.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
      });
    }

    static getSections() {
      var grid = document.getElementById('product-grid');
      var sectionId = grid && grid.dataset ? grid.dataset.id : '';

      return sectionId ? [{ section: sectionId }] : [];
    }

    static getCached(url) {
      return FacetFiltersForm.filterData.find(function (entry) {
        return entry.url === url;
      }) || null;
    }

    static cacheResponse(url, html) {
      FacetFiltersForm.filterData = FacetFiltersForm.filterData
        .filter(function (entry) {
          return entry.url !== url;
        })
        .concat([{ url: url, html: html }])
        .slice(-CACHE_LIMIT);
    }

    static abortPendingRequests() {
      FacetFiltersForm.requestControllers.forEach(function (controller) {
        controller.abort();
      });
      FacetFiltersForm.requestControllers.clear();
    }

    static renderPage(searchParams, event, updateURLHash) {
      var normalizedParams = cleanSearchParams(searchParams);
      var shouldUpdateURL = updateURLHash !== false;
      var sections = FacetFiltersForm.getSections();

      FacetFiltersForm.searchParamsPrev = normalizedParams;
      FacetFiltersForm.renderSequence += 1;
      var sequence = FacetFiltersForm.renderSequence;

      if (shouldUpdateURL) FacetFiltersForm.updateURLHash(normalizedParams);

      if (!sections.length) {
        setLoading(false);
        if (window.location.pathname + window.location.search !== pageUrl(normalizedParams)) {
          window.location.assign(pageUrl(normalizedParams));
        }
        return Promise.resolve(false);
      }

      FacetFiltersForm.abortPendingRequests();
      FacetFiltersForm.toggleActiveFacets(true);
      setLoading(true);

      var tasks = sections.map(function (section) {
        var url = sectionUrl(section.section, normalizedParams);
        var cached = FacetFiltersForm.getCached(url);

        return cached
          ? FacetFiltersForm.renderSectionFromCache(cached, event, sequence)
          : FacetFiltersForm.renderSectionFromFetch(
              url,
              normalizedParams,
              event,
              sequence
            );
      });

      return Promise.all(tasks)
        .then(function () {
          if (sequence !== FacetFiltersForm.renderSequence) return false;
          setLoading(false);
          FacetFiltersForm.toggleActiveFacets(false);
          emit('facets:rendered', {
            searchParams: normalizedParams,
            source: 'section-render',
            version: VERSION
          });
          return true;
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return false;
          if (sequence !== FacetFiltersForm.renderSequence) return false;

          setLoading(false);
          FacetFiltersForm.toggleActiveFacets(false);
          emit('facets:error', {
            searchParams: normalizedParams,
            message: error && error.message ? error.message : 'facets_render_failed',
            version: VERSION
          });
          window.location.assign(pageUrl(normalizedParams));
          return false;
        });
    }

    static renderSectionFromFetch(url, searchParams, event, sequence) {
      var controller = typeof AbortController === 'function'
        ? new AbortController()
        : null;
      if (controller) FacetFiltersForm.requestControllers.set(url, controller);

      var options = {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          Accept: 'text/html',
          'X-Requested-With': 'XMLHttpRequest'
        },
        cache: 'no-store'
      };
      if (controller) options.signal = controller.signal;

      return fetch(url, options)
        .then(function (response) {
          if (!response.ok) {
            throw new Error('facets_section_http_' + response.status);
          }
          return response.text();
        })
        .then(function (html) {
          if (sequence !== FacetFiltersForm.renderSequence) return false;

          var parsedHTML = parseHTML(html);
          if (!parsedHTML.getElementById('ProductGridContainer')) {
            throw new Error('facets_product_grid_missing');
          }

          FacetFiltersForm.cacheResponse(url, html);
          FacetFiltersForm.renderFilters(parsedHTML, event);
          FacetFiltersForm.renderProductGridContainer(parsedHTML);
          FacetFiltersForm.renderProductCount(parsedHTML);

          if (typeof window.initializeScrollAnimationTrigger === 'function') {
            window.initializeScrollAnimationTrigger();
          }

          return true;
        })
        .finally(function () {
          if (controller) FacetFiltersForm.requestControllers.delete(url);
        });
    }

    static renderSectionFromCache(cacheEntry, event, sequence) {
      if (sequence !== FacetFiltersForm.renderSequence) {
        return Promise.resolve(false);
      }

      var parsedHTML = parseHTML(cacheEntry.html);
      if (!parsedHTML.getElementById('ProductGridContainer')) {
        return Promise.reject(new Error('facets_cached_product_grid_missing'));
      }

      FacetFiltersForm.renderFilters(parsedHTML, event);
      FacetFiltersForm.renderProductGridContainer(parsedHTML);
      FacetFiltersForm.renderProductCount(parsedHTML);

      if (typeof window.initializeScrollAnimationTrigger === 'function') {
        window.initializeScrollAnimationTrigger();
      }

      return Promise.resolve(true);
    }

    static renderProductGridContainer(parsedHTML) {
      var source = parsedHTML.getElementById('ProductGridContainer');
      var target = document.getElementById('ProductGridContainer');
      if (!source || !target) throw new Error('facets_product_grid_target_missing');

      target.innerHTML = source.innerHTML;
      target.querySelectorAll('.scroll-trigger').forEach(function (element) {
        element.classList.add('scroll-trigger--cancel');
      });
    }

    static renderProductCount(parsedHTML) {
      var source = parsedHTML.getElementById('ProductCount')
        || parsedHTML.getElementById('ProductCountDesktop');
      var countContainer = document.getElementById('ProductCount');
      var countContainerDesktop = document.getElementById('ProductCountDesktop');

      if (!source) return;
      if (countContainer) {
        countContainer.innerHTML = source.innerHTML;
        countContainer.classList.remove('loading');
      }
      if (countContainerDesktop) {
        countContainerDesktop.innerHTML = source.innerHTML;
        countContainerDesktop.classList.remove('loading');
      }
    }

    static renderFilters(parsedHTML, event) {
      var fetched = Array.prototype.slice.call(
        parsedHTML.querySelectorAll(
          '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
        )
      );
      var current = Array.prototype.slice.call(
        document.querySelectorAll(
          '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
        )
      );
      var eventFilter = event
        && event.target
        && event.target.closest
        ? event.target.closest('.js-filter')
        : null;
      var activeFilterId = eventFilter ? eventFilter.id : '';

      current.forEach(function (currentElement) {
        var stillExists = fetched.some(function (candidate) {
          return candidate.id === currentElement.id;
        });
        if (!stillExists) currentElement.remove();
      });

      var facetsToRender = fetched.filter(function (element) {
        return !activeFilterId || element.id !== activeFilterId;
      });
      var countsToRender = activeFilterId
        ? fetched.find(function (element) {
            return element.id === activeFilterId;
          })
        : null;

      facetsToRender.forEach(function (sourceElement) {
        var targetElement = document.getElementById(sourceElement.id);

        if (targetElement) {
          targetElement.innerHTML = sourceElement.innerHTML;
          return;
        }

        var sourceParent = sourceElement.parentElement;
        var targetParent = sourceParent && sourceParent.id
          ? document.getElementById(sourceParent.id)
          : null;
        if (!targetParent) return;

        var firstFilter = targetParent.querySelector('.js-filter');
        var clone = sourceElement.cloneNode(true);
        if (firstFilter) targetParent.insertBefore(clone, firstFilter);
        else targetParent.appendChild(clone);
      });

      FacetFiltersForm.renderActiveFacets(parsedHTML);
      FacetFiltersForm.renderAdditionalElements(parsedHTML);

      if (!countsToRender || !activeFilterId) return;

      var targetFilter = document.getElementById(activeFilterId);
      if (!targetFilter) return;

      FacetFiltersForm.renderCounts(countsToRender, targetFilter);
      FacetFiltersForm.renderMobileCounts(countsToRender, targetFilter);

      var focusSelector = targetFilter.classList.contains('mobile-facets__details')
        ? '.mobile-facets__close-button'
        : '.facets__summary';
      var focusTarget = targetFilter.querySelector(focusSelector);
      var eventType = event && event.target && event.target.getAttribute
        ? event.target.getAttribute('type')
        : '';

      if (focusTarget && eventType !== 'text' && eventType !== 'number') {
        try {
          focusTarget.focus({ preventScroll: true });
        } catch (_error) {
          focusTarget.focus();
        }
      }
    }

    static renderActiveFacets(parsedHTML) {
      ['.active-facets-mobile', '.active-facets-desktop'].forEach(function (selector) {
        var source = parsedHTML.querySelector(selector);
        var target = document.querySelector(selector);
        if (source && target) target.innerHTML = source.innerHTML;
      });
    }

    static renderAdditionalElements(parsedHTML) {
      ['.mobile-facets__open', '.mobile-facets__count', '.sorting'].forEach(function (selector) {
        var source = parsedHTML.querySelector(selector);
        var target = document.querySelector(selector);
        if (source && target) target.innerHTML = source.innerHTML;
      });
    }

    static renderCounts(source, target) {
      if (!source || !target) return;

      [
        ['.facets__summary', false],
        ['.facets__header', false]
      ].forEach(function (entry) {
        var sourceNode = source.querySelector(entry[0]);
        var targetNode = target.querySelector(entry[0]);
        if (sourceNode && targetNode) {
          targetNode.replaceWith(sourceNode.cloneNode(true));
        }
      });

      var sourceWrap = source.querySelector('.facets-wrap');
      var targetWrap = target.querySelector('.facets-wrap');
      if (!sourceWrap || !targetWrap) return;

      var isShowingMore = Boolean(
        target.querySelector('show-more-button .label-show-more.hidden')
      );
      var replacement = sourceWrap.cloneNode(true);

      if (isShowingMore) {
        replacement.querySelectorAll('.facets__item.hidden').forEach(function (item) {
          item.classList.replace('hidden', 'show-more-item');
        });
      }

      targetWrap.replaceWith(replacement);
    }

    static renderMobileCounts(source, target) {
      if (!source || !target) return;
      var sourceList = source.querySelector('.mobile-facets__list');
      var targetList = target.querySelector('.mobile-facets__list');

      if (sourceList && targetList) {
        targetList.replaceWith(sourceList.cloneNode(true));
      }
    }

    static updateURLHash(searchParams) {
      var normalized = cleanSearchParams(searchParams);
      var url = pageUrl(normalized);

      if (window.location.pathname + window.location.search === url) return;
      window.history.pushState({ searchParams: normalized }, '', url);
    }

    createSearchParams(form) {
      if (!form) return '';
      return new URLSearchParams(new FormData(form)).toString();
    }

    onSubmitForm(searchParams, event) {
      return FacetFiltersForm.renderPage(searchParams, event);
    }

    onSubmitHandler(event) {
      if (!event || !event.target || !event.target.closest) return;
      event.preventDefault();

      var sourceForm = event.target.closest('form') || this.form;
      if (!sourceForm) return;

      var isMobileCheckbox = event.target.classList
        && event.target.classList.contains('mobile-facets__checkbox');

      if (isMobileCheckbox) {
        this.onSubmitForm(this.createSearchParams(sourceForm), event);
        return;
      }

      var isMobile = sourceForm.id === 'FacetFiltersFormMobile';
      var forms = [];

      document.querySelectorAll('facet-filters-form form').forEach((form) => {
        if (isMobile) {
          if (form.id === 'FacetFiltersFormMobile') {
            forms.push(this.createSearchParams(form));
          }
          return;
        }

        if (
          form.id === 'FacetSortForm'
          || form.id === 'FacetFiltersForm'
          || form.id === 'FacetSortDrawerForm'
        ) {
          forms.push(this.createSearchParams(form));
        }
      });

      this.onSubmitForm(
        forms.filter(Boolean).join('&') || this.createSearchParams(sourceForm),
        event
      );
    }

    onActiveFilterClick(event) {
      if (!event || !event.currentTarget) return;
      event.preventDefault();

      if (
        event.currentTarget.classList.contains('disabled')
        || event.currentTarget.getAttribute('aria-disabled') === 'true'
      ) {
        return;
      }

      FacetFiltersForm.toggleActiveFacets(true);

      var href = event.currentTarget.getAttribute('href') || '';
      var url;

      try {
        url = new URL(href, window.location.origin);
      } catch (_error) {
        window.location.assign(href || window.location.pathname);
        return;
      }

      if (url.origin !== window.location.origin) {
        window.location.assign(href);
        return;
      }

      FacetFiltersForm.renderPage(url.searchParams.toString(), event);
    }
  }

  FacetFiltersForm.filterData = [];
  FacetFiltersForm.requestControllers = new Map();
  FacetFiltersForm.searchParamsInitial = cleanSearchParams(window.location.search);
  FacetFiltersForm.searchParamsPrev = FacetFiltersForm.searchParamsInitial;
  FacetFiltersForm.renderSequence = 0;
  FacetFiltersForm.historyListenerInstalled = false;

  if (!customElements.get('facet-filters-form')) {
    customElements.define('facet-filters-form', FacetFiltersForm);
    FacetFiltersForm.setListeners();
  }

  class PriceRange extends HTMLElement {
    constructor() {
      super();
      this.inputs = [];
      this.abortController = null;
      this.cleanups = [];
      this.bound = false;
      this.onRangeChange = this.onRangeChange.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
    }

    connectedCallback() {
      if (this.bound) return;
      this.inputs = Array.prototype.slice.call(this.querySelectorAll('input'));
      if (this.inputs.length < 2) {
        this.setAttribute('data-price-range-state', 'missing-inputs');
        return;
      }

      this.bound = true;
      this.abortController = typeof AbortController === 'function'
        ? new AbortController()
        : null;

      this.inputs.forEach((input) => {
        this.addManagedListener(input, 'change', this.onRangeChange);
        this.addManagedListener(input, 'keydown', this.onKeyDown);
        input.setAttribute('inputmode', 'decimal');
      });

      this.setMinAndMaxValues();
      this.setAttribute('data-price-range-state', 'ready');
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
      this.cleanups.forEach(function (cleanup) {
        cleanup();
      });
      this.cleanups = [];
      this.abortController = null;
      this.bound = false;
    }

    addManagedListener(target, type, listener) {
      var options = this.abortController
        ? { signal: this.abortController.signal }
        : undefined;

      try {
        target.addEventListener(type, listener, options);
      } catch (_error) {
        target.addEventListener(type, listener);
        this.cleanups.push(function () {
          target.removeEventListener(type, listener);
        });
      }
    }

    onRangeChange(event) {
      this.adjustToValidValues(event.currentTarget);
      this.setMinAndMaxValues();
    }

    onKeyDown(event) {
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      var allowed = [
        'Tab',
        'Backspace',
        'Enter',
        'ArrowUp',
        'ArrowDown',
        'ArrowLeft',
        'ArrowRight',
        'Delete',
        'Escape',
        'Home',
        'End',
        '.',
        ',',
        ' '
      ];

      if (/^[0-9]$/.test(event.key) || allowed.indexOf(event.key) !== -1) return;
      event.preventDefault();
    }

    setMinAndMaxValues() {
      var minInput = this.inputs[0];
      var maxInput = this.inputs[1];
      if (!minInput || !maxInput) return;

      var configuredMax = maxInput.getAttribute('data-max')
        || minInput.getAttribute('data-max')
        || '';

      if (maxInput.value !== '') minInput.setAttribute('data-max', maxInput.value);
      else if (configuredMax !== '') minInput.setAttribute('data-max', configuredMax);

      if (minInput.value !== '') maxInput.setAttribute('data-min', minInput.value);
      else maxInput.setAttribute('data-min', '0');
    }

    adjustToValidValues(input) {
      if (!input || input.value === '') return;

      var value = Number(input.value);
      var min = Number(input.getAttribute('data-min'));
      var max = Number(input.getAttribute('data-max'));

      if (!Number.isFinite(value)) {
        input.value = '';
        return;
      }
      if (Number.isFinite(min) && value < min) input.value = String(min);
      if (Number.isFinite(max) && value > max) input.value = String(max);
    }
  }

  if (!customElements.get('price-range')) {
    customElements.define('price-range', PriceRange);
  }

  class FacetRemove extends HTMLElement {
    constructor() {
      super();
      this.link = null;
      this.abortController = null;
      this.cleanups = [];
      this.bound = false;
      this.closeFilter = this.closeFilter.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
    }

    connectedCallback() {
      if (this.bound) return;
      this.link = this.querySelector('a');
      if (!this.link) {
        this.setAttribute('data-facet-remove-state', 'missing-link');
        return;
      }

      this.bound = true;
      this.abortController = typeof AbortController === 'function'
        ? new AbortController()
        : null;
      this.link.setAttribute('role', 'button');
      this.addManagedListener(this.link, 'click', this.closeFilter);
      this.addManagedListener(this.link, 'keydown', this.onKeyDown);
      this.setAttribute('data-facet-remove-state', 'ready');
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
      this.cleanups.forEach(function (cleanup) {
        cleanup();
      });
      this.cleanups = [];
      this.abortController = null;
      this.bound = false;
    }

    addManagedListener(target, type, listener) {
      var options = this.abortController
        ? { signal: this.abortController.signal }
        : undefined;

      try {
        target.addEventListener(type, listener, options);
      } catch (_error) {
        target.addEventListener(type, listener);
        this.cleanups.push(function () {
          target.removeEventListener(type, listener);
        });
      }
    }

    onKeyDown(event) {
      if (event.key !== ' ' && event.code !== 'Space') return;
      event.preventDefault();
      this.closeFilter(event);
    }

    closeFilter(event) {
      if (event) event.preventDefault();
      if (!this.link) return;

      var form = this.closest('facet-filters-form')
        || document.querySelector('facet-filters-form');

      if (form && typeof form.onActiveFilterClick === 'function') {
        form.onActiveFilterClick({
          preventDefault: function () {},
          currentTarget: this.link,
          target: event && event.target ? event.target : this.link
        });
        return;
      }

      window.location.assign(this.link.href);
    }
  }

  if (!customElements.get('facet-remove')) {
    customElements.define('facet-remove', FacetRemove);
  }
})();